import { marker as extractTranslation, marker as translateTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { getLegalRightDictByEntityUuid, getLegalRightKeyTranslation } from '@nk/company-block';
import {
  CompanyLegalRightCompositeEntity,
  CompanyShareOwnershipUltimateCompositeEntity,
  CustomerPersonEntity,
  MonitoringConnectionCompositeEntity,
  PersonCompanyRolePersonFlagCompositeEntity,
  PersonEntity,
  ScreeningEntityScreeningCaseCompositeEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import {
  CardListItemLinkComponent,
  CardListItemTextComponent,
  NkCardListConfig,
  NkCardListData,
  NkCardListItemLink,
  PercentagePipe,
} from '@nk/ui-lib';
import { getLatestScreeningCaseCompositeEntity } from './screening-case-review.utils';
import { getScreeningStatusTags, ScreeningStatusTag } from './screening-overview.utils';
import { ScreeningCaseCardStatusListItemComponent } from '../components/screening-case-card-status-list-item/screening-case-card-status-list-item.component';
import { titleCaseCompanyName } from '@nk/utils-lib';
import { MonitoringConnectionDetailsComponent } from '../components/monitoring-connection-details/monitoring-connection-details.component';
import { ScreeningAddPersonCardListItemComponent } from '../components/screening-add-person-card-list-item/screening-add-person-card-list-item.component';
import { ScreeningRemovePersonCardListItemComponent } from '../components/screening-remove-person-card-list-item/screening-remove-person-card-list-item.component';
import { ScreeningEntitiesInputParams } from './screening-entities.utils';
import { ScreeningRemovePersonParams } from './screening.utils';

export interface MonitoringConnectionCardListItemData extends Partial<ScreeningRemovePersonParams> {
  uuid: string;
  person: WithUuid<PersonEntity> | null;
  customerPerson: WithUuid<CustomerPersonEntity> | null;
}

export interface MonitoringConnectionCardListItem {
  uuid: string;
  entity: NkCardListItemLink;
  screeningStatusTags: ScreeningStatusTag[];
  ownership: string;
  roles: string;
  legalRights: string;
  data: MonitoringConnectionCardListItemData;
}

export const MONITORING_CONNECTION_CARD_LIST_CONFIG: NkCardListConfig<MonitoringConnectionCardListItem> = {
  items: [
    {
      andClass: 'col-sm-3 d-inline-flex justify-content-start align-items-start',
      label: extractTranslation('screening.monitoring_connection.entity'),
      prop: 'entity',
      component: CardListItemLinkComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.screening_overrides.screening_case_table.table.status'),
      prop: 'screeningStatusTags',
      component: ScreeningCaseCardStatusListItemComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.monitoring_connection.ownership'),
      prop: 'ownership',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.monitoring_connection.roles'),
      prop: 'roles',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-1',
      label: extractTranslation('screening.monitoring_connection.legal_rights'),
      prop: 'legalRights',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2 d-inline-flex align-items-center justify-content-end',
      label: '',
      prop: 'data',
      component: ScreeningRemovePersonCardListItemComponent,
      sortable: false,
    },
  ],
  noDataMessage: extractTranslation('screening.monitoring_connection.no_data'),
  detailsComponent: MonitoringConnectionDetailsComponent,
};

export const NO_MONITORING_CONNECTION_ENTITY_CARD_LIST_CONFIG: NkCardListConfig<MonitoringConnectionCardListItem> = {
  items: [
    {
      andClass: 'col-sm-3 d-inline-flex justify-content-start align-items-start',
      label: extractTranslation('screening.monitoring_connection.entity'),
      prop: 'entity',
      component: CardListItemLinkComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.screening_overrides.screening_case_table.table.status'),
      prop: 'screeningStatusTags',
      component: ScreeningCaseCardStatusListItemComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.monitoring_connection.ownership'),
      prop: 'ownership',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2',
      label: extractTranslation('screening.monitoring_connection.roles'),
      prop: 'roles',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-1',
      label: extractTranslation('screening.monitoring_connection.legal_rights'),
      prop: 'legalRights',
      component: CardListItemTextComponent,
      sortable: false,
    },
    {
      andClass: 'col-sm-2 d-inline-flex align-items-center justify-content-end',
      label: '',
      prop: 'data',
      component: ScreeningAddPersonCardListItemComponent,
      sortable: false,
    },
  ],
  noDataMessage: extractTranslation('screening.screening_overrides.not_screened_entities.no_data_found'),
  detailsComponent: MonitoringConnectionDetailsComponent,
};

export const EMPTY_CARD_LIST_DATA: NkCardListData<MonitoringConnectionCardListItem> = {
  entities: [],
  pagination: { limit: 0, page: 0, totalCount: 0 },
};

export function isMonitoringConnectionCompositeEntity(item: unknown): item is MonitoringConnectionCompositeEntity {
  return (
    typeof item === 'object' &&
    item !== null &&
    item.hasOwnProperty('uuid') &&
    item.hasOwnProperty('monitoring_connection') &&
    item.hasOwnProperty('monitoring_origin_entity') &&
    item.hasOwnProperty('screening_entity')
  );
}

export function transformToMonitoringConnectionCardListItems(
  entities: MonitoringConnectionCompositeEntity[],
  ownershipComposites: CompanyShareOwnershipUltimateCompositeEntity[] = [],
  personCompanyRolePersonFlagCompositeEntities: PersonCompanyRolePersonFlagCompositeEntity[] = [],
  legalRightComposites: CompanyLegalRightCompositeEntity[],
  screeningEntityCaseComposites: ScreeningEntityScreeningCaseCompositeEntity[] = [],
  translateService: TranslateService,
  ownershipPercentagePipe: PercentagePipe,
): MonitoringConnectionCardListItem[] {
  return entities
    .slice()
    .sort((a, b) => {
      const aIsCompany = a.company ? 1 : 0;
      const bIsCompany = b.company ? 1 : 0;
      if (aIsCompany !== bIsCompany) return bIsCompany - aIsCompany;
      return sortByOwnership(a, b, ownershipComposites);
    })
    .map((entity) => {
      let entityLink: NkCardListItemLink;

      // Determine entity type and create appropriate link
      if (entity.company) {
        entityLink = {
          routerLink: [`/company/${entity.company.uuid}`],
          label: titleCaseCompanyName(entity.company.name) || entity.company.name || '',
        };
      } else if (entity.person) {
        entityLink = {
          routerLink: [`/person/${entity.person.uuid}`],
          label: entity.person.full_name,
        };
      } else if (entity.customer_person) {
        entityLink = {
          routerLink: [`/screening/customer-person/${entity.customer_person.uuid}`],
          label: entity.customer_person.full_name,
        };
      } else {
        entityLink = {
          routerLink: [],
          label: translateService.instant(translateTranslation('screening.monitoring_connection.unknown_entity')),
        };
      }

      const legalRightDict = getLegalRightDictByEntityUuid(legalRightComposites);
      const entityUuid = entity.company?.uuid || entity.person?.uuid || entity.customer_person?.uuid;

      /*
       * Turns CompanyShareOwnershipUltimateCompositeEntity array into ScreeningCompositeEntity array.
       * There can be cases when ultimate ownership has shareholder_company and not shareholder_person.
       * For example, in case if company has owners from another country.
       * We do not take such cases into consideration currently.
       * That is why we filter only shareholder_person
       */
      const ownership = ownershipPercentagePipe.transform(
        ownershipComposites.filter((i) => i.shareholder_person).find((i) => i.shareholder_person.uuid === entityUuid)
          ?.company_share_ownership_ultimate.ownership || null,
      );

      const roles =
        personCompanyRolePersonFlagCompositeEntities
          .filter((i) => i.person?.uuid === entityUuid)
          .sort((a, b) => a.company_role.company_role_rank - b.company_role.company_role_rank)
          .map((i) => translateService.instant('company_role.' + i.company_role.company_role_key))
          .join(', ') || '-';

      const legalRights = entityUuid
        ? getLegalRightKeyTranslation(legalRightDict[entityUuid], translateService) || '-'
        : '-';

      const screeningEntityComposite = screeningEntityCaseComposites.find(
        (c) => c.screening_entity?.uuid === entity.screening_entity?.uuid,
      );
      const newestCaseComposite = screeningEntityComposite
        ? getLatestScreeningCaseCompositeEntity(screeningEntityComposite.screening_case_composites)
        : null;
      const screeningStatusTags = newestCaseComposite?.screening_entity_screening_case_state
        ? getScreeningStatusTags(newestCaseComposite.screening_entity_screening_case_state)
        : [];

      return {
        uuid: entity.uuid,
        entity: entityLink,
        screeningStatusTags,
        ownership,
        roles,
        legalRights,
        data: {
          uuid: entity.uuid,
          person: entity.person,
          appMembershipUuid: entity.screening_action.app_membership_uuid,
          customerPerson: entity.customer_person,
          screeningConfigUuid: entity.monitoring_origin_entity.screening_config_uuid,
          monitoringOriginEntityUuid: entity.monitoring_origin_entity.uuid,
          screeningEntityUuid: entity.screening_entity.uuid,
          entityMonitoringTypeUuid: entity.screening_action?.entity_monitoring_type_uuid,
        },
      };
    });
}

/**
 * Sort by raw ownership in descending order (highest first)
 */
export function sortByOwnership<T extends { person: PersonEntity | null }>(
  a: T,
  b: T,
  ownershipComposites: CompanyShareOwnershipUltimateCompositeEntity[],
): number {
  const rawOwnershipA =
    ownershipComposites.find((i) => i.shareholder_person?.uuid === a.person?.uuid)?.company_share_ownership_ultimate
      .ownership ?? 0;
  const rawOwnershipB =
    ownershipComposites.find((i) => i.shareholder_person?.uuid === b.person?.uuid)?.company_share_ownership_ultimate
      .ownership ?? 0;
  return rawOwnershipB - rawOwnershipA;
}

export function mapToNoMonitoringConnectionEntityCompositeCardListItems(
  entities: PersonCompanyRolePersonFlagCompositeEntity[],
  ownershipComposites: CompanyShareOwnershipUltimateCompositeEntity[] = [],
  legalRightComposites: CompanyLegalRightCompositeEntity[],
  currentlyScreeningDict: Record<string, true>,
  screeningEntitiesInputParams: ScreeningEntitiesInputParams,
  translateService: TranslateService,
  ownershipPercentagePipe: PercentagePipe,
): MonitoringConnectionCardListItem[] {
  // Group entities by person.uuid
  const groupedByPersonUuid = entities.reduce(
    (acc, entity) => {
      const personUuid = entity.person.uuid;
      if (!acc[personUuid]) {
        acc[personUuid] = {
          person: entity.person,
          roles: [],
        };
      }
      acc[personUuid].roles.push(entity);
      return acc;
    },
    {} as Record<
      string,
      {
        person: PersonCompanyRolePersonFlagCompositeEntity['person'];
        roles: PersonCompanyRolePersonFlagCompositeEntity[];
      }
    >,
  );

  // Convert grouped data to card list items and sort by ownership in descending order
  return Object.values(groupedByPersonUuid)
    .filter(({ person }) => !currentlyScreeningDict[person.uuid])
    .sort((a, b) => sortByOwnership(a, b, ownershipComposites))
    .map(({ person, roles }) => {
      /*
       * There can be cases when ultimate ownership has shareholder_company and not shareholder_person.
       * For example, in case if company has owners from another country.
       * We do not take such cases into consideration currently.
       * That is why we filter only shareholder_person
       */
      const ownership = ownershipPercentagePipe.transform(
        ownershipComposites.filter((i) => i.shareholder_person).find((i) => i.shareholder_person.uuid === person.uuid)
          ?.company_share_ownership_ultimate.ownership || null,
      );

      // Join roles for this person and sort by company_role_rank
      const rolesText =
        roles
          .sort((a, b) => a.company_role.company_role_rank - b.company_role.company_role_rank)
          .map((role) => translateService.instant('company_role.' + role.company_role.company_role_key))
          .join(', ') || '-';

      const legalRightDict = getLegalRightDictByEntityUuid(legalRightComposites);
      const legalRights = person.uuid
        ? getLegalRightKeyTranslation(legalRightDict[person.uuid], translateService) || '-'
        : '-';

      return {
        uuid: person.uuid,
        entity: {
          routerLink: [`/person/${person.uuid}`],
          label: person.full_name,
        },
        screeningStatusTags: [],
        ownership,
        roles: rolesText,
        legalRights,
        data: {
          uuid: person.uuid,
          person,
          customerPerson: null,
          screeningConfigUuid: screeningEntitiesInputParams.screeningConfigUuid,
          monitoringOriginEntityUuid: screeningEntitiesInputParams.monitoringOriginEntityUuid,
          screeningEntityUuid: undefined,
          entityMonitoringTypeUuid: undefined,
        },
      };
    });
}
